import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  AlertTriangle, 
  Truck,
  RotateCcw,
  RefreshCw,
  Search,
  Filter,
  Loader2,
  MapPin,
  Phone,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  ExternalLink,
  ChevronDown,
  MessageSquare,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NDRDashboardPage = () => {
  const navigate = useNavigate();
  const [ndrList, setNdrList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedNdr, setSelectedNdr] = useState(null);
  const [actionType, setActionType] = useState('reattempt');
  const [actionNotes, setActionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    reattempt_requested: 0,
    rto_requested: 0,
    resolved: 0
  });

  useEffect(() => {
    fetchNdrList();
  }, [statusFilter]);

  const fetchNdrList = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(
        `${API_BASE_URL}/couriers/ndr?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNdrList(response.data.ndr_list || []);
      setStats(response.data.stats || stats);
    } catch (error) {
      console.error('Error fetching NDR list:', error);
      toast.error('Failed to fetch NDR data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchNdrList();
  };

  const openActionDialog = (ndr, type) => {
    setSelectedNdr(ndr);
    setActionType(type);
    setActionNotes('');
    setActionDialogOpen(true);
  };

  const handleSubmitAction = async () => {
    if (!selectedNdr) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/v1/couriers/ndr/${selectedNdr.id}/action`,
        { 
          action: actionType,
          notes: actionNotes,
          new_address: actionType === 'reattempt' ? selectedNdr.delivery_address : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${actionType === 'reattempt' ? 'Reattempt' : 'RTO'} request submitted`);
      setActionDialogOpen(false);
      fetchNdrList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit action');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending' },
      reattempt_requested: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Reattempt Requested' },
      rto_requested: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'RTO Requested' },
      reattempt_scheduled: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Reattempt Scheduled' },
      delivered: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Delivered' },
      rto_completed: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'RTO Completed' },
      failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Failed' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={`${config.bg} ${config.text} border-none`}>
        {config.label}
      </Badge>
    );
  };

  const getReasonIcon = (reason) => {
    const iconMap = {
      'customer_not_available': Clock,
      'wrong_address': MapPin,
      'refused': XCircle,
      'phone_unreachable': Phone,
      'other': AlertTriangle
    };
    return iconMap[reason] || AlertTriangle;
  };

  const filteredNdrList = ndrList.filter(ndr => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ndr.order_number?.toLowerCase().includes(query) ||
      ndr.awb_number?.toLowerCase().includes(query) ||
      ndr.customer_name?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/orders')}
            className="border-wt-border"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-600">
              <AlertTriangle className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-wt-text-primary">NDR Dashboard</h1>
              <p className="text-wt-text-muted">Manage non-delivery reports & failed attempts</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Bluedart has no NDR API. Operators raise reattempt /
              RTO / address-change requests in the per-shipment NDR
              dialog; we log them in db.ndr_requests and they
              download this Excel sheet to forward to their Bluedart
              account manager. */}
          <Button
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const r = await fetch(
                  `${API_BASE_URL}/couriers/ndr/bluedart-export?status=submitted&days=90`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const blob = await r.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `bluedart-ndr-actions-${new Date().toISOString().slice(0, 10)}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Bluedart NDR sheet downloaded');
              } catch (e) {
                toast.error(`Export failed: ${e.message}`);
              }
            }}
            variant="outline"
            className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
            data-testid="export-bluedart-ndr-btn"
            title="Download Excel of Bluedart NDR actions to forward to your KAM (Bluedart has no public NDR API)"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Bluedart NDR
          </Button>

          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            className="border-wt-border"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-4"
        >
          <div className="text-2xl font-bold text-wt-text-primary">{stats.total}</div>
          <div className="text-sm text-wt-text-muted">Total NDRs</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4"
        >
          <div className="text-2xl font-bold text-yellow-400">{stats.pending}</div>
          <div className="text-sm text-yellow-400/70">Pending Action</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4"
        >
          <div className="text-2xl font-bold text-blue-400">{stats.reattempt_requested}</div>
          <div className="text-sm text-blue-400/70">Reattempt Requested</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4"
        >
          <div className="text-2xl font-bold text-orange-400">{stats.rto_requested}</div>
          <div className="text-sm text-orange-400/70">RTO Requested</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-green-500/10 border border-green-500/20 rounded-xl p-4"
        >
          <div className="text-2xl font-bold text-green-400">{stats.resolved}</div>
          <div className="text-sm text-green-400/70">Resolved</div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
          <Input
            placeholder="Search by order #, AWB, or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-wt-bg border-wt-border text-wt-text-primary">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reattempt_requested">Reattempt Requested</SelectItem>
            <SelectItem value="rto_requested">RTO Requested</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="rto_completed">RTO Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* NDR List */}
      {filteredNdrList.length === 0 ? (
        <div className="bg-wt-surface border border-wt-border rounded-xl p-12 text-center">
          <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-400 opacity-50" />
          <h3 className="text-xl font-semibold text-wt-text-primary mb-2">No NDRs Found</h3>
          <p className="text-wt-text-muted">
            {statusFilter !== 'all' 
              ? 'No NDRs match the selected filter'
              : 'All courier deliveries are successful! Great job!'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNdrList.map((ndr, idx) => {
            const ReasonIcon = getReasonIcon(ndr.reason);
            
            return (
              <motion.div
                key={ndr.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5 hover:border-wt-gold/30 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left: Order Info */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-red-500/10">
                      <ReasonIcon className="h-6 w-6 text-red-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-semibold text-wt-text-primary">
                          {ndr.order_number}
                        </span>
                        {getStatusBadge(ndr.status)}
                      </div>
                      <div className="text-sm text-wt-text-secondary mb-2">
                        AWB: <span className="text-wt-gold">{ndr.awb_number}</span>
                        <span className="mx-2">•</span>
                        {ndr.courier_type?.toUpperCase()}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-wt-text-muted">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(ndr.failed_at).toLocaleDateString()}
                        </span>
                        <span>Attempt #{ndr.attempt_count || 1}</span>
                      </div>
                    </div>
                  </div>

                  {/* Center: Customer & Reason */}
                  <div className="flex-1 lg:text-center">
                    <div className="font-medium text-wt-text-primary">{ndr.customer_name}</div>
                    <div className="text-sm text-wt-text-muted flex items-center gap-1 lg:justify-center">
                      <Phone className="h-3 w-3" />
                      {ndr.customer_phone}
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-wt-text-muted">Reason: </span>
                      <span className="text-red-400 capitalize">
                        {ndr.reason?.replace(/_/g, ' ') || 'Not specified'}
                      </span>
                    </div>
                    {ndr.courier_remarks && (
                      <div className="text-xs text-wt-text-muted mt-1 italic">
                        "{ndr.courier_remarks}"
                      </div>
                    )}
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-2">
                    {ndr.status === 'pending' && (
                      <>
                        <Button
                          onClick={() => openActionDialog(ndr, 'reattempt')}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                          size="sm"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Reattempt
                        </Button>
                        <Button
                          onClick={() => openActionDialog(ndr, 'rto')}
                          variant="outline"
                          className="border-orange-500 text-orange-400 hover:bg-orange-500/10"
                          size="sm"
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          RTO
                        </Button>
                      </>
                    )}
                    {ndr.status !== 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/orders?search=${ndr.order_number}`)}
                        className="text-wt-text-muted"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Order
                      </Button>
                    )}
                  </div>
                </div>

                {/* Action History */}
                {ndr.actions?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-wt-border">
                    <div className="text-xs text-wt-text-muted mb-2">Action History</div>
                    <div className="flex flex-wrap gap-2">
                      {ndr.actions.map((action, i) => (
                        <div 
                          key={i}
                          className="text-xs px-2 py-1 bg-wt-subtle rounded"
                        >
                          <span className="capitalize">{action.type}</span>
                          <span className="mx-1">•</span>
                          <span>{new Date(action.timestamp).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              {actionType === 'reattempt' ? (
                <>
                  <RefreshCw className="h-5 w-5 text-blue-400" />
                  Request Reattempt
                </>
              ) : (
                <>
                  <RotateCcw className="h-5 w-5 text-orange-400" />
                  Request RTO (Return to Origin)
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedNdr && (
            <div className="space-y-4 pt-4">
              <div className="bg-wt-surface rounded-lg p-4 border border-wt-border">
                <div className="text-sm text-wt-text-muted mb-1">Order</div>
                <div className="font-medium text-wt-text-primary">{selectedNdr.order_number}</div>
                <div className="text-sm text-wt-text-secondary mt-1">
                  AWB: {selectedNdr.awb_number}
                </div>
              </div>

              {actionType === 'reattempt' && (
                <div>
                  <Label className="text-wt-text-primary">Delivery Address</Label>
                  <div className="mt-1 p-3 bg-wt-bg rounded-lg border border-wt-border text-sm text-wt-text-secondary">
                    {selectedNdr.delivery_address || 'No address on file'}
                  </div>
                  <p className="text-xs text-wt-text-muted mt-1">
                    The courier will reattempt delivery to this address
                  </p>
                </div>
              )}

              {actionType === 'rto' && (
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-orange-300">
                      <strong>Warning:</strong> Requesting RTO will return this shipment to your warehouse. 
                      This action may incur return shipping charges.
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-wt-text-primary">Notes (Optional)</Label>
                <Textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  placeholder={actionType === 'reattempt' 
                    ? 'Any special instructions for the delivery...' 
                    : 'Reason for RTO...'}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              onClick={() => setActionDialogOpen(false)}
              className="text-wt-text-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAction}
              disabled={submitting}
              className={actionType === 'reattempt' 
                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                : 'bg-orange-500 hover:bg-orange-600 text-white'}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : actionType === 'reattempt' ? (
                <RefreshCw className="h-4 w-4 mr-2" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              {actionType === 'reattempt' ? 'Request Reattempt' : 'Request RTO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NDRDashboardPage;
